import { GoogleMap, GoogleMapProps } from '@react-google-maps/api'
import { useMemo } from 'react'

import { useGoogleMaps } from 'src/providers/GoogleMapsProvider'

import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'

import { ClusterPoints } from './ClusterPoints'
import { MultiPointMapGeo } from './types'

export interface MultiPointMapProps {
  geoData: MultiPointMapGeo[]
  zoom: number
  mapProps?: GoogleMapProps
  constrainMap?: boolean
  clustered?: boolean
  renderInfoContent?: (point: MultiPointMapGeo) => JSX.Element
  pinIcon?: string
}
export const DEFAULT_CENTER = { lat: -41.2865, lng: 174.7762 }

const NEW_ZEALAND_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: -30.36,
  south: -51.35,
  west: 162.28,
  east: -175.81
}
const DEFAULT_MAP_PROPS: GoogleMapProps = {
  center: DEFAULT_CENTER,
  mapContainerStyle: {
    width: '100%',
    height: '400px'
  },
  options: {
    mapTypeControl: true,
    mapTypeId: 'satellite',
    streetViewControl: false,
    disableDefaultUI: true,
    fullscreenControl: true,
    zoomControl: true
  }
}

export const MultiPointMap = ({
  geoData,
  zoom,
  mapProps = {},
  constrainMap,
  clustered,
  renderInfoContent,
  pinIcon
}: MultiPointMapProps) => {
  const finalMapProps = useMemo<GoogleMapProps>(() => {
    const mergedProps = {
      ...DEFAULT_MAP_PROPS,
      ...mapProps,
      mapContainerStyle: {
        ...DEFAULT_MAP_PROPS.mapContainerStyle,
        ...mapProps.mapContainerStyle
      },
      options: {
        ...DEFAULT_MAP_PROPS.options,
        ...mapProps.options
      }
    }

    if (constrainMap) {
      return {
        ...mergedProps,
        options: {
          ...mergedProps.options,
          minZoom: 5,
          maxZoom: 15,
          restriction: {
            latLngBounds: NEW_ZEALAND_BOUNDS
          }
        }
      }
    }
    return mergedProps
  }, [constrainMap, mapProps])

  const { isLoaded } = useGoogleMaps()
  return (
    <LoadingSpinner isLoading={!isLoaded}>
      <GoogleMap
        {...finalMapProps}
        onLoad={map => {
          const markers = geoData.filter(g => !!g.place_id)
          if (markers.length > 1) {
            const bounds = new google.maps.LatLngBounds()
            markers.forEach(m => bounds.extend({ lng: m.lng, lat: m.lat }))
            map.fitBounds(bounds)
            return
          }
          if (markers.length === 1) {
            const { lat, lng } = markers[0]!
            map.setCenter({
              lat,
              lng
            })
            map.setZoom(zoom)
            return
          }
          map.setCenter(DEFAULT_CENTER)
          map.setZoom(zoom)
        }}
      >
        <ClusterPoints
          pointData={geoData}
          clustered={clustered}
          pinIcon={pinIcon}
          renderInfoContent={renderInfoContent}
        />
      </GoogleMap>
    </LoadingSpinner>
  )
}
